import { useMemo } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useTripsList } from "@b2bportal/core-trips";
import { TRIPS_PATH } from "@b2bportal/core-utilities";
import type { HotelItinerarySummary } from "@b2bportal/lodging-api";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { useHotelTripProduct } from "@tenant/hotels";
import { Section, SectionTitle, SectionDescription } from "../Section";
import { UpcomingTripCard } from "./UpcomingTripCard";
import styles from "./UpcomingTripsSection.module.scss";
import {
  hotelItineraryRoute,
  useUpcomingTripNavigation,
} from "./useUpcomingTripNavigation";
import { useCarsTripProduct } from "@/pages/Trips/hooks/useCarsTripProduct";
import type { GroundBookingContext } from "@/utils/commerce/types";

export const UpcomingTripsSection = () => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "UpcomingTripsSection");
  const handleTripSelect = useUpcomingTripNavigation();

  const { hotels } = useHotelTripProduct({
    tripsPath: TRIPS_PATH,
    reservationPath: hotelItineraryRoute,
  });

  const { cars } = useCarsTripProduct();

  const { context: tripsContext } = useTripsList({
    products: { ...hotels, ...cars },
  });

  const trips = useMemo(() => {
    if (!tripsContext.trips) return [];
    return tripsContext.trips.slice(0, 2);
  }, [tripsContext.trips]);

  if (trips.length === 0) {
    return null;
  }
  return (
    <Section className={block}>
      <div className={cn("header")}>
        <SectionTitle as="h1">{t("home.upcomingTrips.title")}</SectionTitle>
        <SectionDescription className={cn("description")}>
          <div>
            <Trans
              i18nKey={"home.upcomingTrips.seeAll"}
              components={[
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a className={cn("link")} href={TRIPS_PATH} />,
              ]}
            />
          </div>
        </SectionDescription>
      </div>
      <div className={cn("list")}>
        {trips.map((wrapper) => (
          <UpcomingTripCard
            key={wrapper.reservationId}
            reservationId={wrapper.reservationId}
            reservation={
              wrapper.reservation as
                | HotelItinerarySummary
                | GroundBookingContext
            }
            productType={wrapper.productType}
            onSelectTrip={(reservationId) =>
              handleTripSelect(
                reservationId,
                wrapper.productType,
                wrapper.reservation as
                  | HotelItinerarySummary
                  | GroundBookingContext
              )
            }
          />
        ))}
      </div>
    </Section>
  );
};
